import React, { useEffect } from 'react';
import { Button, Flex, Modal, Typography, theme } from 'antd';
import { CheckCircleOutlined, ExclamationCircleTwoTone, Loading3QuartersOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import DiagnosticsStepper from './DiagnosticsStepper';
import RerunPlanGate from './RerunPlanGate';
import { VISIBLE_STEPS, getCompletedStepKeys } from '../diagnosticsStepMap';

// Figma uses custom semi-transparent backgrounds — no matching Ant Design tokens exist.
const CARD_BG_PROGRESS = '#E8F4E64D';
const CARD_BG_ERROR = '#FFEDE94D';
const ICON_SIZE = 48;

export const COMPLETION_REDIRECT_DELAY_MS = 2500;

const BOUNCE_IN_KEYFRAMES = `
@keyframes bounce-in {
  0% { transform: scale(0); }
  60% { transform: scale(1.15); }
  100% { transform: scale(1); }
}`;

const CLOSE_BUTTON_OFFSET = `
.diagnostics-progress-modal .ant5-modal-close {
  top: 24px;
  right: 24px;
}`;

type DiagnosticsProgressModalProps = {
  open: boolean;
  domain: string;
  loading: boolean;
  checks: Partial<Record<string, boolean>>;
  error?: string;
  reportId?: string;
  onRerun: () => void;
  onClose: () => void;
};

export default function DiagnosticsProgressModal({
  open,
  domain,
  loading,
  checks,
  error,
  reportId,
  onRerun,
  onClose,
}: DiagnosticsProgressModalProps) {
  const navigate = useNavigate();
  const { token } = theme.useToken();

  const isComplete = !loading && !error && !!reportId;
  const completedKeys = getCompletedStepKeys(checks);

  useEffect(() => {
    if (isComplete && reportId) {
      const timer = setTimeout(() => {
        navigate(`/ai-insights/diagnostics/report/${reportId}`);
      }, COMPLETION_REDIRECT_DELAY_MS);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [isComplete, reportId, navigate]);

  return (
    <Modal
      open={open}
      title={null}
      closable={!!error}
      onCancel={onClose}
      footer={null}
      width={640}
      rootClassName="diagnostics-progress-modal"
    >
      <style>{BOUNCE_IN_KEYFRAMES}{CLOSE_BUTTON_OFFSET}</style>
      {error ? (
        <Flex
          vertical
          align="center"
          justify="center"
          gap={24}
          style={{
            backgroundColor: CARD_BG_ERROR,
            borderRadius: token.borderRadiusLG,
            height: 400,
          }}
        >
          <ExclamationCircleTwoTone
            twoToneColor={token.colorError}
            style={{ fontSize: ICON_SIZE }}
          />
          <Typography.Text strong style={{ fontSize: token.fontSizeLG, color: token.colorText }}>
            Something went wrong and your diagnostics didn&apos;t finish
          </Typography.Text>
          <RerunPlanGate
            render={(rerunDisabled) => (
              <Button
                type="primary"
                disabled={rerunDisabled}
                onClick={onRerun}
              >
                Rerun diagnostics
              </Button>
            )}
          />
        </Flex>
      ) : (
        <Flex
          vertical
          align="center"
          justify="center"
          style={{
            backgroundColor: CARD_BG_PROGRESS,
            borderRadius: token.borderRadiusLG,
            height: 400,
          }}
        >
          <Flex vertical align="center" gap={24} style={{ padding: '0 16px' }}>
            {isComplete ? (
              <CheckCircleOutlined
                style={{
                  fontSize: ICON_SIZE,
                  color: token.colorPrimary,
                  animation: 'bounce-in 0.4s ease-out',
                }}
              />
            ) : (
              <Loading3QuartersOutlined spin style={{ fontSize: ICON_SIZE, color: token.colorTextSecondary }} />
            )}
            <Flex vertical align="center" gap={4}>
              <Typography.Text strong style={{ fontSize: token.fontSizeLG, color: token.colorText }}>
                Diagnostics running for {domain}
              </Typography.Text>
              <Typography.Text style={{ fontSize: token.fontSizeLG, color: token.colorText, textAlign: 'center' }}>
                Please stay on this page. Leaving will cancel the diagnostics.
              </Typography.Text>
            </Flex>
            <DiagnosticsStepper steps={VISIBLE_STEPS} completedKeys={completedKeys} />
          </Flex>
        </Flex>
      )}
    </Modal>
  );
}
